
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAssistantActions } from '@/components/assistant/assistant-provider';
import { useMediaQuery } from '@/hooks/use-media-query';
import { organizationApi } from '@/lib/api/auth';
import { dealTypesApi } from '@/lib/api/crm';
import {
  industryTemplates,
  type DealTypeTemplate,
  type IndustryTemplate,
} from '@/lib/onboarding/industry-templates';
import type { CustomFieldsSchema } from '@/types/crm';
import { IndustrySelector } from './IndustrySelector';
import { DealTypeConfigurator } from './DealTypeConfigurator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const onboardingSchema = z.object({
  name: z.string().min(2, 'Workspace name must be at least 2 characters'),
  description: z.string().optional(),
});

type OnboardingFormData = z.infer<typeof onboardingSchema>;
type Step = 'create-org' | 'select-industry' | 'configure-deal-types';

interface OnboardingModalProps {
  open: boolean;
  onComplete: () => void | Promise<void>;
  /** When set, skips org creation and starts at 'select-industry' */
  initialStep?: 'select-industry';
}

export function OnboardingModal({ open, onComplete, initialStep }: OnboardingModalProps) {
  const { loadOrganizations, switchOrganization } = useAuth();
  const assistant = useAssistantActions();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const startStep = initialStep ?? 'create-org';
  const [step, setStep] = useState<Step>(startStep);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndustry, setSelectedIndustry] =
    useState<IndustryTemplate | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dealTypes, setDealTypes] = useState<DealTypeTemplate[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OnboardingFormData>({
    resolver: zodResolver(onboardingSchema),
  });

  useEffect(() => {
    if (open) {
      reset();
      setError('');
      setStep(startStep);
      setSelectedIndustry(null);
      setSelectedId(null);
      setDealTypes([]);
    }
  }, [open, reset, startStep]);

  const onCreateOrg = async (data: OnboardingFormData) => {
    try {
      setError('');
      setIsLoading(true);
      const org = await organizationApi.create({
        name: data.name,
        description: data.description || undefined,
      });
      // Switch to the new org so the JWT has orgId for deal type creation
      await loadOrganizations();
      await switchOrganization(org.id);
      setStep('select-industry');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create workspace'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onSelectIndustry = (industry: IndustryTemplate | null) => {
    setSelectedIndustry(industry);
    if (industry) {
      // Deep-copy templates so edits don't mutate the original data
      setDealTypes(
        industry.dealTypes.map((dt) => ({
          ...dt,
          customFields: dt.customFields.map((f) => ({ ...f })),
        }))
      );
      setStep('configure-deal-types');
    } else {
      finishOnboarding([]);
    }
  };

  // Desktop advances immediately on card tap (single-tap contract);
  // mobile only records the selection and waits for Continue.
  const handleIndustrySelectedIdChange = (id: string) => {
    setSelectedId(id);
    if (isDesktop) {
      const industry = industryTemplates.find((i) => i.id === id);
      if (industry) onSelectIndustry(industry);
    }
  };

  const onContinueFromIndustry = () => {
    const industry = industryTemplates.find((i) => i.id === selectedId);
    if (industry) onSelectIndustry(industry);
  };

  const onStartWithAi = () => {
    if (assistant) {
      assistant.openAssistantOnboarding();
    } else {
      sessionStorage.setItem("assistant:onboarding-pending", "1");
    }
    void onComplete();
  };

  const onConfirmDealTypes = () => {
    const validDealTypes = dealTypes.filter((dt) => dt.name.trim());
    finishOnboarding(validDealTypes);
  };

  const finishOnboarding = async (typesToCreate: DealTypeTemplate[]) => {
    try {
      setError('');
      setIsLoading(true);

      for (const dt of typesToCreate) {
        const schema: CustomFieldsSchema | undefined =
          dt.customFields.length > 0
            ? {
                fields: dt.customFields
                  .filter((f) => f.name.trim())
                  .map((f) =>
                    f.options
                      ? { ...f, options: f.options.filter(Boolean) }
                      : f
                  ),
              }
            : undefined;

        await dealTypesApi.create({
          name: dt.name,
          description: dt.description || undefined,
          customFieldsSchema: schema,
        });
      }

      await onComplete();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create deal types'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const skipOrgCreation = !!initialStep;
  const steps: Step[] = skipOrgCreation
    ? ['select-industry', 'configure-deal-types']
    : ['create-org', 'select-industry', 'configure-deal-types'];
  const stepIndex = steps.indexOf(step);

  const titles: Record<Step, { title: string; description: string }> = {
    'create-org': {
      title: 'Welcome to CRM! 🎉',
      description: "Let's create your first workspace to get started",
    },
    'select-industry': {
      title: skipOrgCreation ? 'Set Up Your Workspace' : 'Choose Your Industry',
      description:
        'Pick a template to pre-configure your deal types, or start from scratch',
    },
    'configure-deal-types': {
      title: 'Customize Deal Types',
      description: `Review and edit the ${selectedIndustry?.name || ''} deal types before we create them`,
    },
  };

  const errorBox = error ? (
    <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">
      {error}
      <p className="mt-1 text-xs">
        Please try again or refresh the page if the problem persists.
      </p>
    </div>
  ) : null;

  const createOrgFields = (
    <>
      <div className="space-y-2">
        <Label htmlFor="name">Workspace Name *</Label>
        <Input
          id="name"
          placeholder="My Company"
          {...register('name')}
        />
        {errors.name && (
          <p className="text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          placeholder="Describe your workspace..."
          rows={3}
          {...register('description')}
        />
      </div>
    </>
  );

  const industryStep = (
    <IndustrySelector
      industries={industryTemplates}
      onSelect={onSelectIndustry}
      selectedId={selectedId}
      onSelectedIdChange={handleIndustrySelectedIdChange}
      onStartWithAi={onStartWithAi}
    />
  );

  const dealTypesStep = (
    <DealTypeConfigurator dealTypes={dealTypes} onChange={setDealTypes} />
  );

  // All call sites mount the modal unconditionally with open={...};
  // the desktop Dialog renders nothing when closed, so must the mobile overlay.
  if (!open) {
    return null;
  }

  if (!isDesktop) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-background app-safe-top">
        {/* Progress */}
        <div className="flex items-center gap-1.25 px-5 pt-4">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === stepIndex
                  ? 'w-[22px] bg-primary'
                  : i < stepIndex
                    ? 'w-2 bg-primary/40'
                    : 'w-2 bg-muted'
              }`}
            />
          ))}
          <span className="ml-auto text-xs text-muted-foreground">
            Step {stepIndex + 1} of {steps.length}
          </span>
        </div>

        <div className="px-5 pt-5">
          <h2 className="text-2xl font-semibold tracking-tight">
            {titles[step].title}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {titles[step].description}
          </p>
        </div>

        {errorBox && <div className="px-5 pt-4">{errorBox}</div>}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-4">
          {step === 'create-org' && (
            <form
              id="onboarding-create-org-form"
              onSubmit={handleSubmit(onCreateOrg)}
              className="space-y-4"
            >
              {createOrgFields}
            </form>
          )}
          {step === 'select-industry' && industryStep}
          {step === 'configure-deal-types' && dealTypesStep}
        </div>

        <div className="flex shrink-0 gap-2.5 border-t p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {stepIndex > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(steps[stepIndex - 1])}
              disabled={isLoading}
            >
              Back
            </Button>
          )}
          {step === 'create-org' && (
            <Button
              type="submit"
              form="onboarding-create-org-form"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? 'Creating workspace...' : 'Continue'}
            </Button>
          )}
          {step === 'select-industry' && (
            <Button
              type="button"
              className="flex-1"
              onClick={onContinueFromIndustry}
              disabled={isLoading || selectedId === null}
            >
              Continue
            </Button>
          )}
          {step === 'configure-deal-types' && (
            <Button
              type="button"
              className="flex-1"
              onClick={onConfirmDealTypes}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating deal types...
                </>
              ) : (
                `Confirm & Create ${dealTypes.filter((dt) => dt.name.trim()).length} Deal Types`
              )}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && skipOrgCreation) onComplete(); }}>
      <DialogContent
        showCloseButton={skipOrgCreation}
        onInteractOutside={(e) => { if (!skipOrgCreation) e.preventDefault(); }}
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-[600px]"
      >
        {/* Step indicator */}
        <div className="flex justify-center gap-2 pt-2">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === stepIndex
                  ? 'w-8 bg-primary'
                  : i < stepIndex
                    ? 'w-8 bg-primary/40'
                    : 'w-8 bg-muted'
              }`}
            />
          ))}
        </div>

        <DialogHeader>
          <DialogTitle>{titles[step].title}</DialogTitle>
          <DialogDescription>{titles[step].description}</DialogDescription>
        </DialogHeader>

        {errorBox}

        {/* Step 1: Create Workspace */}
        {step === 'create-org' && (
          <form onSubmit={handleSubmit(onCreateOrg)} className="space-y-4">
            {createOrgFields}
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Creating workspace...' : 'Continue'}
            </Button>
          </form>
        )}

        {/* Step 2: Choose Industry */}
        {step === 'select-industry' && industryStep}

        {/* Step 3: Configure Deal Types */}
        {step === 'configure-deal-types' && (
          <div className="space-y-4">
            {dealTypesStep}
            <div className="flex gap-2 pt-2 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('select-industry')}
                disabled={isLoading}
              >
                Back
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={onConfirmDealTypes}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating deal types...
                  </>
                ) : (
                  `Confirm & Create ${dealTypes.filter((dt) => dt.name.trim()).length} Deal Types`
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
